import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { STATUS_TONE, healthTone } from "@/pages/Standorte";
import { Stack, Heartbeat, Wrench, Plus, MapTrifold, ArrowElbowDownRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const Kpi = ({ label, value, tone, testid }) => (
  <Card data-testid={testid} className="rounded-md border p-4">
    <Label2>{label}</Label2>
    <div className={`mt-1 font-heading text-2xl font-black tracking-tighter ${tone || ""}`}>{value}</div>
  </Card>
);

const groupTypes = (types) => types.reduce((m, t) => { const k = t.kategorie || "Sonstiges"; (m[k] = m[k] || []).push(t); return m; }, {});

export default function StandortDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ov, setOv] = useState(null);
  const [dash, setDash] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [planStart, setPlanStart] = useState("");
  const [planMonteur, setPlanMonteur] = useState("");
  const [planOpen, setPlanOpen] = useState(false);
  const [selAssets, setSelAssets] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [assetOpen, setAssetOpen] = useState(false);
  const [af, setAf] = useState({ name: "", kundenbezeichnung: "", asset_type_key: "", parent_id: "none", create_default_components: true });

  const load = useCallback(() => {
    api.get(`/v1/sites/${id}/overview`).then((r) => setOv(r.data));
    api.get(`/v1/sites/${id}/dashboard`).then((r) => setDash(r.data));
    api.get("/v1/catalogs/asset-types").then((r) => setAssetTypes(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const planJob = async () => {
    if (!planStart || selAssets.length === 0) return;
    try {
      const { data } = await api.post("/v1/maintenance-jobs", { site_id: id, zeitraum_start: planStart, monteur_name: planMonteur || null, asset_ids: selAssets });
      toast.success("Wartungsauftrag angelegt", { description: `${data.progress.total} Anlagen` });
      setPlanOpen(false);
      navigate(`/wartungsauftraege/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPlan = (o) => {
    setPlanOpen(o);
    if (o) { setSelAssets(ov.assets.map((a) => a.id)); setTypeFilter("all"); }
  };
  const toggleSel = (aid) => setSelAssets((s) => s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]);

  const addAsset = async () => {
    const at = assetTypes.find((t) => t.key === af.asset_type_key);
    if (!af.name || !at) return;
    try {
      const { data } = await api.post("/assets", {
        name: af.name, asset_type: at.name, asset_type_key: at.key,
        kundenbezeichnung: af.kundenbezeichnung || null,
        site_id: id, customer_id: ov.site.customer_id || null,
        parent_id: af.parent_id === "none" ? null : af.parent_id,
        create_default_components: af.create_default_components,
      });
      toast.success("Anlage angelegt", { description: (data.components_created || []).length ? `${data.components_created.length} Komponenten erzeugt` : undefined });
      setAssetOpen(false); setAf({ name: "", kundenbezeichnung: "", asset_type_key: "", parent_id: "none", create_default_components: true });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!ov || !dash) return <div className="space-y-4"><Skeleton className="h-24 rounded-md" /><Skeleton className="h-64 rounded-md" /></div>;
  const k = dash.kpis;
  const site = ov.site;
  const plan = site.floorplan_url || null;
  const typeGroups = groupTypes(assetTypes);
  // Anlagenbaum iterativ abflachen (DFS) -> Liste aus { asset, depth }
  const byId = Object.fromEntries(ov.assets.map((a) => [a.id, a]));
  const childrenOf = (aid) => ov.assets.filter((a) => a.parent_id === aid);
  const roots = ov.assets.filter((a) => !a.parent_id || !byId[a.parent_id]);
  const flatAssets = [];
  const walk = (list, depth) => list.forEach((a) => { flatAssets.push({ a, depth }); walk(childrenOf(a.id), depth + 1); });
  walk(roots, 0);

  return (
    <div>
      <PageHeader title={site.name} subtitle={`${site.address || "Standort"} · Gesundheit ${ov.site_health}%`}>
        <Dialog open={assetOpen} onOpenChange={setAssetOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="gap-2" data-testid="add-asset-btn"><Plus size={16} weight="bold" /> Anlage hinzufügen</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-asset-dialog">
            <DialogHeader><DialogTitle className="font-heading">Neue Anlage · {site.name}</DialogTitle></DialogHeader>
            <DialogDescription>Anlagentyp aus dem Katalog wählen – Standard-Komponenten werden optional automatisch erzeugt.</DialogDescription>
            <div className="space-y-3">
              <div><Label2>Bezeichnung</Label2><Input data-testid="asset-name" value={af.name} onChange={(e) => setAf({ ...af, name: e.target.value })} placeholder="z. B. Verladestelle 01" /></div>
              <div><Label2>Kundenbezeichnung / Interne Anlagenbezeichnung</Label2><Input data-testid="asset-kundenbez" value={af.kundenbezeichnung} onChange={(e) => setAf({ ...af, kundenbezeichnung: e.target.value })} placeholder="z. B. Tor 07 · Halle B Tor 2 · T-17" /></div>
              <div><Label2>Anlagentyp</Label2>
                <Select value={af.asset_type_key} onValueChange={(v) => setAf({ ...af, asset_type_key: v })}>
                  <SelectTrigger data-testid="asset-type"><SelectValue placeholder="Typ wählen" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeGroups).map(([kat, list]) => (
                      <SelectGroup key={kat}><SelectLabel>{kat}</SelectLabel>
                        {list.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label2>Übergeordnete Anlage (optional)</Label2>
                <Select value={af.parent_id} onValueChange={(v) => setAf({ ...af, parent_id: v })}>
                  <SelectTrigger data-testid="asset-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Keine (eigenständige Anlage) —</SelectItem>
                    {ov.assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox data-testid="asset-default-comps" checked={af.create_default_components} onCheckedChange={(v) => setAf({ ...af, create_default_components: !!v })} />
                Standard-Komponenten des Typs automatisch anlegen
              </label>
            </div>
            <DialogFooter><Button data-testid="asset-save" onClick={addAsset} disabled={!af.name || !af.asset_type_key}>Anlage anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={planOpen} onOpenChange={openPlan}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2" data-testid="plan-job-btn"><Wrench size={16} weight="bold" /> Wartung planen</Button>
          </DialogTrigger>
          <DialogContent data-testid="plan-job-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Standort-Wartung planen</DialogTitle></DialogHeader>
            <DialogDescription>Wähle die heute zu wartenden Anlagen. Das passende Prüfprotokoll wird je Anlage automatisch geladen.</DialogDescription>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label2>Beginn</Label2><Input data-testid="plan-start" type="date" value={planStart} onChange={(e) => setPlanStart(e.target.value)} /></div>
                <div><Label2>Monteur (optional)</Label2><Input data-testid="plan-monteur" placeholder="z. B. Max Berger" value={planMonteur} onChange={(e) => setPlanMonteur(e.target.value)} /></div>
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <Label2>Anlagen ({selAssets.length}/{ov.assets.length})</Label2>
                  <div className="flex items-center gap-1.5">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="h-7 w-40 text-xs" data-testid="plan-type-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Typen</SelectItem>
                        {[...new Set(ov.assets.map((a) => a.asset_type).filter(Boolean))].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid="plan-select-all" onClick={() => setSelAssets(ov.assets.map((a) => a.id))}>Alle</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid="plan-select-none" onClick={() => setSelAssets([])}>Keine</Button>
                  </div>
                </div>
                <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {ov.assets.filter((a) => typeFilter === "all" || a.asset_type === typeFilter).map((a) => (
                    <label key={a.id} data-testid={`plan-asset-${a.id}`} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent">
                      <Checkbox checked={selAssets.includes(a.id)} onCheckedChange={() => toggleSel(a.id)} />
                      <span className="min-w-0 flex-1 truncate">{a.name} <span className="text-xs text-muted-foreground">· {a.asset_type || "—"}</span></span>
                      <Badge variant="outline" className={`rounded-md text-[10px] ${STATUS_TONE[a.status] || ""}`}>{a.status}</Badge>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="plan-save" onClick={planJob} disabled={!planStart || selAssets.length === 0}>Auftrag anlegen ({selAssets.length} Anlagen)</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi testid="kpi-anlagen" label="Anlagen" value={k.anlagen} />
        <Kpi testid="kpi-komponenten" label="Komponenten" value={k.komponenten} />
        <Kpi testid="kpi-wartungen" label="Offene Wartungen" value={k.offene_wartungen} />
        <Kpi testid="kpi-massnahmen" label="Offene Maßnahmen" value={k.offene_massnahmen} />
        <Kpi testid="kpi-sicherheit" label="Sicherheitsmängel" value={k.sicherheitsrelevante_maengel} tone={k.sicherheitsrelevante_maengel > 0 ? "text-red-600" : ""} />
        <Kpi testid="kpi-health" label="Gesundheit" value={`${k.site_health}%`} tone={healthTone(k.site_health)} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Card className="rounded-md border p-4" data-testid="hall-plan">
          <div className="mb-3 flex items-center gap-2"><MapTrifold size={18} weight="duotone" className="text-primary" /><Label2>Hallen-/Standortplan</Label2></div>
          {plan ? (
            <div className="relative overflow-hidden rounded-md border">
              <img src={plan} alt="Hallenplan" className="w-full object-cover" style={{ maxHeight: 420 }} />
              {ov.assets.map((a) => a.position && (
                <button key={a.id} data-testid={`marker-${a.id}`} onClick={() => navigate(`/anlage/${a.id}`)}
                  title={`${a.name} · ${a.status}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md transition-transform hover:scale-125"
                  style={{ left: `${a.position.x}%`, top: `${a.position.y}%`, width: 18, height: 18,
                    background: a.status === "Betriebsbereit" ? "#16a34a" : (a.status.includes("Sicher") || a.status === "Außer Betrieb") ? "#dc2626" : a.status === "Modernisierung empfohlen" ? "#2563eb" : "#f59e0b" }} />
              ))}
            </div>
          ) : <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">Kein Hallenplan hinterlegt.</div>}
          <p className="mt-2 text-xs text-muted-foreground">Marker farbcodiert nach Anlagenstatus · Klick öffnet die Anlagenakte.</p>
        </Card>

        <Card className="rounded-md border" data-testid="site-assets">
          <div className="border-b border-border p-4"><Label2>Anlagen ({ov.asset_count})</Label2></div>
          <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
            {ov.assets.length === 0 && <div className="p-6 text-sm text-muted-foreground">Noch keine Anlagen. Über „Anlage hinzufügen" anlegen.</div>}
            {flatAssets.map(({ a, depth }) => (
              <button key={a.id} data-testid={`asset-row-${a.id}`} onClick={() => navigate(`/anlage/${a.id}`)}
                className="flex w-full items-center justify-between gap-3 py-3 pr-4 text-left transition-colors hover:bg-accent"
                style={{ paddingLeft: 16 + depth * 20 }}>
                <div className="flex min-w-0 items-center gap-2">
                  {depth > 0 && <ArrowElbowDownRight size={14} className="shrink-0 text-muted-foreground" />}
                  <Stack size={18} weight="duotone" className="shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.asset_type || "—"} · {a.components_total} Komponenten</div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className={`flex items-center gap-1 text-xs font-bold ${healthTone(a.health_index)}`}><Heartbeat size={13} weight="fill" />{a.health_index}%</span>
                  <Badge variant="outline" className={`rounded-md text-xs ${STATUS_TONE[a.status] || ""}`}>{a.status}</Badge>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
